import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { supabase } from "../../lib/supabase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDarkMode } from "../../contexts/DarkModeContext";

// Floating Dark Mode Toggle Component
function FloatingDarkModeToggle() {
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={toggleDarkMode}
      className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200"
      aria-label="Toggle dark mode"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3, delay: 1 }}
    >
      {isDarkMode ? (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </motion.button>
  );
}

const AdminDashboard = () => {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [isGoalsDropdownOpen, setIsGoalsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get filter values from URL parameters
  const statusFilter = searchParams.get("statusFilter") || "all";
  const experienceFilter = searchParams.get("experienceFilter") || "all";
  const backgroundFilter = searchParams.get("backgroundFilter") || "all";
  const selectedGoals = searchParams.get("selectedGoals") ? searchParams.get("selectedGoals").split(",") : [];
  const groupByTeam = searchParams.get("groupByTeam") === "true";
  const searchQuery = searchParams.get("searchQuery") || "";
  const heardAboutFilter = searchParams.get("heardAboutFilter") || "all";

  // Helper function to update URL parameters
  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === "all" || value === "" || value === false || (Array.isArray(value) && value.length === 0)) {
      newParams.delete(key);
    } else {
      newParams.set(key, Array.isArray(value) ? value.join(",") : value.toString());
    }
    setSearchParams(newParams);
  };

  // Available goals from registration form
  const availableGoals = [
    "Getting a job opportunity",
    "Networking with professionals",
    "Learning new AI skills",
    "Building a product/project",
    "Meeting potential co-founders",
    "Winning prize money",
    "Earning recognition for innovation",
    "Gaining experience in competitions",
    "Contributing to open source",
    "Personal portfolio development",
    "Meeting industry mentors",
    "Other"
  ];

  useEffect(() => {
    checkAuth();
    fetchApplicants();
    // Initialize with all goals selected if none are in URL
    if (!searchParams.get("selectedGoals")) {
      updateFilter("selectedGoals", availableGoals);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isGoalsDropdownOpen && !event.target.closest('.goals-dropdown')) {
        setIsGoalsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGoalsDropdownOpen]);

  const checkAuth = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/admin/login");
        return;
      }
      setUser(user);
    } catch (error) {
      console.error("Auth check error:", error);
      navigate("/admin/login");
    }
  };

  const fetchApplicants = async () => {
    try {
      setLoading(true);
      
      // Fetch applicants
      const { data: applicantsData, error: applicantsError } = await supabase
        .from("applicants")
        .select("*")
        .order("created_at", { ascending: false });

      if (applicantsError) {
        throw applicantsError;
      }

      // Fetch final decisions
      const { data: finalDecisionsData, error: finalDecisionsError } = await supabase
        .from("final_decision")
        .select("applicant_id, decision");

      if (finalDecisionsError) {
        throw finalDecisionsError;
      }

      // Create a map of final decisions by applicant_id
      const finalDecisionsMap = {};
      (finalDecisionsData || []).forEach(decision => {
        finalDecisionsMap[decision.applicant_id] = decision.decision;
      });

      // Add final decision status to each applicant
      const applicantsWithFinalDecision = (applicantsData || []).map(applicant => ({
        ...applicant,
        final_decision_status: finalDecisionsMap[applicant.id] || null
      }));

      setApplicants(applicantsWithFinalDecision);
    } catch (error) {
      console.error("Error fetching applicants:", error);
      setError(`Failed to load applicants: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const updateApplicantStatus = async (applicantId, newStatus) => {
    try {
      const { error } = await supabase
        .from("applicants")
        .update({ status: newStatus })
        .eq("id", applicantId);

      if (error) throw error;

      // Update local state
      setApplicants((prev) =>
        prev.map((app) =>
          app.id === applicantId ? { ...app, status: newStatus } : app
        )
      );
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const exportToCSV = async () => {
    try {
      setLoading(true);
      
      // Use filtered applicants instead of fetching all data
      // First, get admin decisions for the filtered applicants
      const filteredApplicantIds = filteredApplicants.map(app => app.id);
      
      let adminDecisionsData = [];
      if (filteredApplicantIds.length > 0) {
        const { data, error: adminDecisionsError } = await supabase
          .from("admin_decisions")
          .select("applicant_id, admin_email, decision")
          .in("applicant_id", filteredApplicantIds);

        if (adminDecisionsError) {
          throw adminDecisionsError;
        }
        adminDecisionsData = data || [];
      }

      // Add admin decisions to filtered applicants
      const applicantsData = filteredApplicants.map(applicant => ({
        ...applicant,
        admin_decisions: adminDecisionsData.filter(decision => decision.applicant_id === applicant.id)
      }));

      // Get all unique admin emails to create dynamic columns
      const allAdminEmails = new Set();
      applicantsData.forEach(applicant => {
        applicant.admin_decisions?.forEach(decision => {
          allAdminEmails.add(decision.admin_email);
        });
      });
      const adminEmails = Array.from(allAdminEmails).sort();

      // Create CSV headers
      const baseHeaders = [
        'Full Name',
        'Email', 
        'Phone',
        'LinkedIn Profile',
        'Location',
        'Can Attend In Person',
        'Role/Position',
        'Professional Background',
        'Years of Experience', 
        'T-Shirt Size',
        'Dietary Restrictions',
        'How Heard About Event',
        'Team Participation',
        'Team Name',
        'Team Members',
        'Goals',
        'AI Question',
        'Current Status',
        'Application Date'
      ];
      
      // Add admin decision columns
      const adminHeaders = adminEmails.map(email => `${email} Decision`);
      const headers = [...baseHeaders, ...adminHeaders];

      // Process applicant data
      const csvData = applicantsData.map(applicant => {
        // Parse team members if it exists
        let teamMembersText = '';
        if (applicant.team_members) {
          try {
            const members = JSON.parse(applicant.team_members);
            teamMembersText = members.map(member => 
              `${member.name} (${member.email}) - ${member.role}`
            ).join('; ');
          } catch (e) {
            teamMembersText = applicant.team_members;
          }
        }

        // Create admin decisions map
        const adminDecisions = {};
        applicant.admin_decisions?.forEach(decision => {
          adminDecisions[decision.admin_email] = decision.decision;
        });

        // Base applicant data
        const baseData = [
          applicant.full_name || '',
          applicant.email || '',
          applicant.phone || '',
          applicant.linkedin_profile || '',
          applicant.location || '',
          applicant.can_attend_in_person ? 'Yes' : 'No',
          applicant.role || '',
          applicant.professional_background || '',
          applicant.professional_experience || '',
          applicant.tshirt_size || '',
          applicant.dietary_restrictions || '',
          applicant.heard_about_event || '',
          applicant.team_participation || '',
          applicant.team_name || '',
          teamMembersText,
          applicant.goals || '',
          applicant.ai_question || '',
          applicant.status || '',
          applicant.created_at ? new Date(applicant.created_at).toLocaleDateString() : ''
        ];

        // Add admin decision data
        const adminData = adminEmails.map(email => adminDecisions[email] || '');
        
        return [...baseData, ...adminData];
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => 
          row.map(cell => {
            // Escape CSV content - wrap in quotes if contains comma, quote, or newline
            const cellStr = String(cell || '');
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(',')
        )
      ].join('\n');

      // Download CSV with filtered data indication in filename
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const isFiltered = filteredApplicants.length < applicants.length;
      const filename = `buildathon-applicants${isFiltered ? '-filtered' : ''}-${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError(`Failed to export CSV: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "reviewed":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "accepted":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "final_accept":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "final_reject":
        return "bg-rose-100 text-rose-800 border-rose-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const filteredApplicants = applicants.filter((app) => {
    const matchesStatus = statusFilter === "all" || 
      app.status === statusFilter ||
      (statusFilter === "final_accept" && app.final_decision_status === "final_accept") ||
      (statusFilter === "final_reject" && app.final_decision_status === "final_reject");

    const matchesExperience =
      experienceFilter === "all" ||
      app.professional_experience === `${experienceFilter} years`;

    const matchesBackground =
      backgroundFilter === "all" ||
      app.professional_background?.includes(backgroundFilter);

    // Search by name - case insensitive
    const matchesSearch = searchQuery === "" || 
      app.full_name?.toLowerCase().includes(searchQuery.toLowerCase());

    // Filter by heard_about_event
    const matchesHeardAbout = heardAboutFilter === "all" || 
      app.heard_about_event === heardAboutFilter;

    // Goals filtering - check if any of the applicant's goals match selected goals
    const matchesGoals = selectedGoals.length === 0 || selectedGoals.length === availableGoals.length || (() => {
      if (!app.goals) return false;
      
      // Parse comma-separated goals and trim whitespace
      const applicantGoals = app.goals.split(',').map(goal => goal.trim());
      
      // Check if any applicant goal matches any selected goal
      return applicantGoals.some(applicantGoal => 
        selectedGoals.some(selectedGoal => 
          applicantGoal.toLowerCase().includes(selectedGoal.toLowerCase()) ||
          selectedGoal.toLowerCase().includes(applicantGoal.toLowerCase())
        )
      );
    })();

    return matchesStatus && matchesExperience && matchesBackground && matchesSearch && matchesHeardAbout && matchesGoals;
  });

  // Goals filter handlers
  const handleGoalToggle = (goal) => {
    const newGoals = selectedGoals.includes(goal) 
      ? selectedGoals.filter(g => g !== goal)
      : [...selectedGoals, goal];
    updateFilter("selectedGoals", newGoals);
  };

  const handleToggleAllGoals = () => {
    if (selectedGoals.length === availableGoals.length) {
      updateFilter("selectedGoals", []);
    } else {
      updateFilter("selectedGoals", availableGoals);
    }
  };

  // Navigate to applicant with navigation context
  const navigateToApplicant = (applicantId, index) => {
    // Create URL with navigation context
    const navParams = new URLSearchParams(searchParams);
    navParams.set("fromList", "true");
    navParams.set("currentIndex", index.toString());
    navParams.set("totalCount", filteredApplicants.length.toString());
    
    navigate(`/admin/applicant/${applicantId}?${navParams.toString()}`);
  };

  // Simple team grouping logic
  // Get unique team names from filtered applicants
  const uniqueTeamNames = [...new Set(
    filteredApplicants
      .filter(app => app.team_name && app.team_name.trim() !== "")
      .map(app => app.team_name)
  )];

  // Create team groups
  const teamGroups = uniqueTeamNames.map(teamName => ({
    teamName: teamName,
    members: filteredApplicants.filter(app => app.team_name === teamName)
  }));

  // Get individual applicants (no team)
  const individualApplicants = filteredApplicants.filter(
    app => !app.team_name || app.team_name.trim() === ""
  );

  const statuses = ["pending", "reviewed", "accepted", "rejected", "final_accept", "final_reject"];

  // Function to render applicant table
  const renderApplicantTable = (applicants, title) => {
    if (applicants.length === 0) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title} (0)
            </h3>
          </div>
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              No {title.toLowerCase()} found.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title} ({applicants.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/4">
                  Name & Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/5">
                  Role & Experience
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/4">
                  Team & Attendance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/12">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                  Applied
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {applicants.map((applicant, index) => (
                <tr
                  key={applicant.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => navigateToApplicant(applicant.id, index)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {applicant.full_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {applicant.email}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {applicant.phone}
                      </div>
                      {applicant.linkedin_profile && (
                        <div className="text-xs text-blue-600 dark:text-blue-400">
                          LinkedIn Profile
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div
                        className="text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate"
                        title={applicant.role}
                      >
                        {applicant.role}
                      </div>
                      <div
                        className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate"
                        title={applicant.professional_background}
                      >
                        {applicant.professional_background}
                      </div>
                      <div className="text-xs text-purple-600 dark:text-purple-400">
                        {applicant.professional_experience}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        T-shirt: {applicant.tshirt_size}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {applicant.team_participation}
                      </div>
                      {applicant.team_name && (
                        <div className="text-xs text-green-600 dark:text-green-400">
                          Team: {applicant.team_name}
                        </div>
                      )}
                      {applicant.team_members && (
                        <div className="text-xs text-blue-600 dark:text-blue-400">
                          {JSON.parse(applicant.team_members).length} team
                          members
                        </div>
                      )}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Via: {applicant.heard_about_event}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {applicant.final_decision_status ? (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                          applicant.final_decision_status
                        )}`}
                      >
                        {applicant.final_decision_status === "final_accept" ? "Final Accept" : "Final Reject"}
                      </span>
                    ) : (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                          applicant.status
                        )}`}
                      >
                        {applicant.status.charAt(0).toUpperCase() +
                          applicant.status.slice(1)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(applicant.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aifund mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading applicants...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Admin Dashboard
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Buildathon Applications
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Welcome, {user?.email}
              </span>
              <motion.button
                onClick={exportToCSV}
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                  loading 
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Exporting...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV
                  </div>
                )}
              </motion.button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
            <p className="text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Total
            </h3>
            <p className="text-2xl font-bold text-aifund">
              {applicants.length}
            </p>
          </div>
          {statuses.map((status) => (
            <div
              key={status}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm"
            >
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 capitalize">
                {status === "final_accept" ? "Final Accept" : status === "final_reject" ? "Final Reject" : status}
              </h3>
              <p className="text-2xl font-bold text-aifund">
                {status === "final_accept" 
                  ? applicants.filter((app) => app.final_decision_status === "final_accept").length
                  : status === "final_reject" 
                  ? applicants.filter((app) => app.final_decision_status === "final_reject").length
                  : applicants.filter((app) => app.status === status).length
                }
              </p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm mb-6">
          <div className="flex items-center justify-between flex-wrap gap-y-4">
            <div className="flex items-center space-x-6 flex-wrap gap-y-4">
              {/* Search by name */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search by name:
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => updateFilter("searchQuery", e.target.value)}
                  placeholder="Enter name..."
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[200px]"
                />
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Filter by status:
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => updateFilter("statusFilter", e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">
                    All Applications ({applicants.length})
                  </option>
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status === "final_accept" ? "Final Accept" : status === "final_reject" ? "Final Reject" : status.charAt(0).toUpperCase() + status.slice(1)} (
                      {status === "final_accept" 
                        ? applicants.filter((app) => app.final_decision_status === "final_accept").length
                        : status === "final_reject" 
                        ? applicants.filter((app) => app.final_decision_status === "final_reject").length
                        : applicants.filter((app) => app.status === status).length
                      }
                      )
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Filter by experience:
                </label>
                <select
                  value={experienceFilter}
                  onChange={(e) => updateFilter("experienceFilter", e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Experience Levels</option>
                  <option value="0-2">0-2 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10-20">10-20 years</option>
                  <option value="20+">20+ years</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Filter by background:
                </label>
                <select
                  value={backgroundFilter}
                  onChange={(e) => updateFilter("backgroundFilter", e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Backgrounds</option>
                  <option value="Entrepreneur">Entrepreneur</option>
                  <option value="Executive/Management">
                    Executive/Management
                  </option>
                  <option value="Individual Contributor">
                    Individual Contributor
                  </option>
                  <option value="Student">Student</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Heard about event:
                </label>
                <select
                  value={heardAboutFilter}
                  onChange={(e) => updateFilter("heardAboutFilter", e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Sources</option>
                  <option value="Twitter/X">Twitter/X</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="DeepLearning.AI">DeepLearning.AI</option>
                  <option value="AI Link">AI Link</option>
                  <option value="Friend or Colleague">Friend or Colleague</option>
                  <option value="University/School">University/School</option>
                  <option value="Company/Work">Company/Work</option>
                  <option value="AI Fund Website">AI Fund Website</option>
                  <option value="Newsletter/Email">Newsletter/Email</option>
                  <option value="Tech Community/Forum">Tech Community/Forum</option>
                  <option value="Conference/Event">Conference/Event</option>
                  <option value="Search Engine">Search Engine</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="flex items-center space-x-2 relative">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Filter by goals:
                </label>
                <div className="relative goals-dropdown">
                  <button
                    onClick={() => setIsGoalsDropdownOpen(!isGoalsDropdownOpen)}
                    className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm flex items-center justify-between min-w-[200px]"
                  >
                    <span>
                      {selectedGoals.length === availableGoals.length 
                        ? "All goals" 
                        : `${selectedGoals.length} goals selected`}
                    </span>
                    <svg 
                      className={`w-4 h-4 ml-2 transition-transform ${isGoalsDropdownOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isGoalsDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-80 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
                      {/* All checkbox */}
                      <div className="p-2 border-b border-gray-200 dark:border-gray-600">
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={selectedGoals.length === availableGoals.length}
                            onChange={handleToggleAllGoals}
                            className="mr-2"
                          />
                          <span className="font-medium">All Goals</span>
                        </label>
                      </div>
                      
                      {/* Individual goal checkboxes */}
                      <div className="p-2">
                        {availableGoals.map((goal, index) => (
                          <label key={index} className="flex items-center text-sm py-1">
                            <input
                              type="checkbox"
                              checked={selectedGoals.includes(goal)}
                              onChange={() => handleGoalToggle(goal)}
                              className="mr-2"
                            />
                            <span>{goal}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Group by Team Toggle */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Group by Teams:
              </label>
              <button
                onClick={() => updateFilter("groupByTeam", !groupByTeam)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                  groupByTeam ? "bg-aifund" : "bg-gray-200 dark:bg-gray-600"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                    groupByTeam ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {groupByTeam ? "On" : "Off"}
              </span>
            </div>
          </div>
        </div>

        {/* Conditional Rendering Based on Toggle */}
        {groupByTeam ? (
          <>
            {/* Applications Overview */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Applications ({filteredApplicants.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Individual Applicants
                  </h3>
                  <p className="text-2xl font-bold text-blue-600">
                    {individualApplicants.length}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Teams
                  </h3>
                  <p className="text-2xl font-bold text-green-600">
                    {teamGroups.length}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Team Members
                  </h3>
                  <p className="text-2xl font-bold text-purple-600">
                    {teamGroups.reduce(
                      (total, team) => total + team.members.length,
                      0
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Teams */}
            {teamGroups.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Teams ({teamGroups.length})
                </h2>
                {teamGroups.map((team) => (
                  <div key={team.teamName} className="mb-6">
                    {renderApplicantTable(
                      team.members,
                      `Team: ${team.teamName}`
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Individual Applicants */}
            {renderApplicantTable(
              individualApplicants,
              "Individual Applicants"
            )}
          </>
        ) : (
          /* Regular Table View */
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Applications ({filteredApplicants.length})
              </h2>
            </div>

            {filteredApplicants.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">
                  {statusFilter === "all"
                    ? "No applications yet."
                    : `No ${statusFilter} applications.`}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/4">
                        Name & Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/5">
                        Role & Experience
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/4">
                        Team & Attendance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/12">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/6">
                        Applied
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredApplicants.map((applicant, index) => (
                      <tr
                        key={applicant.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => navigateToApplicant(applicant.id, index)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {applicant.full_name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {applicant.email}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {applicant.phone}
                            </div>
                            {applicant.linkedin_profile && (
                              <div className="text-xs text-blue-600 dark:text-blue-400">
                                LinkedIn Profile
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div
                              className="text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate"
                              title={applicant.role}
                            >
                              {applicant.role}
                            </div>
                            <div
                              className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate"
                              title={applicant.professional_background}
                            >
                              {applicant.professional_background}
                            </div>
                            <div className="text-xs text-purple-600 dark:text-purple-400">
                              {applicant.professional_experience}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              T-shirt: {applicant.tshirt_size}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {applicant.team_participation}
                            </div>
                            {applicant.team_name && (
                              <div className="text-xs text-green-600 dark:text-green-400">
                                Team: {applicant.team_name}
                              </div>
                            )}
                            {applicant.team_members && (
                              <div className="text-xs text-blue-600 dark:text-blue-400">
                                {JSON.parse(applicant.team_members).length} team
                                members
                              </div>
                            )}
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Via: {applicant.heard_about_event}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {applicant.final_decision_status ? (
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                applicant.final_decision_status
                              )}`}
                            >
                              {applicant.final_decision_status === "final_accept" ? "Final Accept" : "Final Reject"}
                            </span>
                          ) : (
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                applicant.status
                              )}`}
                            >
                              {applicant.status.charAt(0).toUpperCase() +
                                applicant.status.slice(1)}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(applicant.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Floating Dark Mode Toggle */}
      <FloatingDarkModeToggle />
    </div>
  );
};

export default AdminDashboard;
